import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, LayoutGrid, List } from "lucide-react";
import { useLicitaciones } from "@/hooks/use-licitaciones";
import type { EstadoLicitacion, TipoContrato, Prioridad, LicitacionFilters } from "@/types/licitacion";
import { LicitacionSearch } from "@/components/licitaciones/licitacion-search";
import { LicitacionFilters as Filters } from "@/components/licitaciones/licitacion-filters";
import { LicitacionList } from "@/components/licitaciones/licitacion-list";
import { Pagination } from "@/components/ui/pagination";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorState } from "@/components/ui/error-state";

export default function LicitacionesPage() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<"cards" | "table">("table");
  const [filters, setFilters] = useState<LicitacionFilters>({
    page: 1,
    page_size: 12,
    sort_by: "created_at",
    sort_order: "desc",
  });
  const [search, setSearch] = useState("");
  const [estado, setEstado] = useState<EstadoLicitacion | "">("");
  const [tipoContrato, setTipoContrato] = useState<TipoContrato | "">("");
  const [prioridad, setPrioridad] = useState<Prioridad | "">("");

  const effectiveFilters: LicitacionFilters = {
    ...filters,
    search: search || undefined,
    estado: estado || undefined,
    tipo_contrato: tipoContrato || undefined,
  };

  const { data, isLoading, isError, error } = useLicitaciones(effectiveFilters);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setFilters((p) => ({ ...p, page: 1 }));
  }, []);

  const handleEstadoChange = useCallback((v: EstadoLicitacion | "") => {
    setEstado(v);
    setFilters((p) => ({ ...p, page: 1 }));
  }, []);

  const handleTipoContratoChange = useCallback((v: TipoContrato | "") => {
    setTipoContrato(v);
    setFilters((p) => ({ ...p, page: 1 }));
  }, []);

  const handlePrioridadChange = useCallback((v: Prioridad | "") => {
    setPrioridad(v);
    setFilters((p) => ({ ...p, page: 1 }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setEstado("");
    setTipoContrato("");
    setPrioridad("");
    setSearch("");
    setFilters((p) => ({ ...p, page: 1 }));
  }, []);

  const handleSort = useCallback((field: string) => {
    setFilters((p) => ({
      ...p,
      sort_by: field,
      sort_order: p.sort_by === field && p.sort_order === "asc" ? "desc" : "asc",
    }));
  }, []);

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Licitaciones{data ? ` (${data.total})` : ""}
          </h1>
          <p className="text-sm text-gray-500 mt-1">Gestiona y consulta todas las licitaciones</p>
        </div>
        <button
          onClick={() => navigate("/licitaciones/nueva")}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nueva
        </button>
      </div>

      {/* Search */}
      <LicitacionSearch value={search} onChange={handleSearchChange} />

      {/* Filters + View toggle */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Filters
          estado={estado}
          tipoContrato={tipoContrato}
          prioridad={prioridad}
          onEstadoChange={handleEstadoChange}
          onTipoContratoChange={handleTipoContratoChange}
          onPrioridadChange={handlePrioridadChange}
          onClear={handleClearFilters}
        />
        <div className="flex items-center gap-1 border border-gray-200 rounded-lg p-0.5">
          <button
            onClick={() => setViewMode("cards")}
            className={`p-1.5 rounded ${viewMode === "cards" ? "bg-gray-100 text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
            aria-label="Vista tarjetas"
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode("table")}
            className={`p-1.5 rounded ${viewMode === "table" ? "bg-gray-100 text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
            aria-label="Vista tabla"
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : isError ? (
        <ErrorState
          title="Error al cargar"
          message={error instanceof Error ? error.message : "Error al cargar licitaciones"}
        />
      ) : (
        <>
          <LicitacionList
            licitaciones={data?.data ?? []}
            viewMode={viewMode}
            onSort={handleSort}
            sortBy={filters.sort_by}
            sortOrder={filters.sort_order}
            onCreateClick={() => navigate("/licitaciones/nueva")}
          />

          {data && data.total_pages > 1 && (
            <Pagination
              page={data.page}
              totalPages={data.total_pages}
              totalItems={data.total}
              onPageChange={(page) => setFilters((p) => ({ ...p, page }))}
            />
          )}
        </>
      )}
    </div>
  );
}
